/**
 * ============================================================================
 * TREACT — Thin Reactive Client Framework
 * ============================================================================
 *
 * A minimal client-side runtime where the server returns a JSON stream of
 * "actions" that the client applies to the DOM. The client never holds
 * application state; it's a view. The server is the truth.
 *
 *   Wire format:   POST {handler, data}      →  JSON [Action, Action, ...]
 *   Transport:     HTTP(S) — and SSE for server-pushed event streams
 *   Substrate:     jQuery 3.x
 *
 *   Companion files:
 *     helpers.php             — PHP reference SDK (the t*() builder functions)
 *     handler.php             — PHP reference HTTP dispatcher (customizable)
 *     treact.css              — default styles (modal overlay, etc.)
 *     treact.checkmistakes.js — optional DOM-hygiene linter (loaded in debug mode)
 *
 * ============================================================================
 * ACTION VOCABULARY  (everything the server can emit in its JSON response)
 * ============================================================================
 *
 * @typedef {Object} Action
 * @property {string}  action   — verb name (case-insensitive; aliases share executors)
 * @property {number} [delay]   — ms to wait before executing (CUMULATIVE with prior actions)
 *
 * Every action accepts `delay`. Most accept `selector` (jQuery selector for the target).
 * The selector substring "_self" is rewritten to "#<id-of-triggering-element>".
 *
 * ── DOM mutation ──
 *   {action: 'addClass',        selector, className}
 *   {action: 'removeClass',     selector, className}
 *   {action: 'toggleClass',     selector, className}
 *   {action: 'setContent',      selector, content}    .html()  — sets inner HTML
 *                                                     aliases: html, replace, replaceElement,
 *                                                     replaceInner, updateContent, setcontent
 *   {action: 'replaceWith',     selector, content}    .replaceWith()  — outer replace
 *                                                     aliases: replaceAll, replaceOuter
 *   {action: 'append',          selector, content}    alias: appendContent
 *   {action: 'prepend',         selector, content}    alias: prependContent
 *   {action: 'appendAfter',     selector, content}    alias: insertAfter
 *   {action: 'setValue',        selector, value}
 *   {action: 'setAttribute',    selector, attribute, value}    alias: attr
 *   {action: 'removeAttribute', selector, attribute}           alias: removeAttr
 *   {action: 'setProp',         selector, prop, value}         aliases: prop, setProperty
 *   {action: 'removeProp',      selector, prop}                alias: removeProperty
 *   {action: 'css',             selector, css}                 alias: addCss
 *   {action: 'remove',          selector}                      alias: removeElement
 *   {action: 'focus',           selector}
 *   {action: 'focusselect',     selector}                      focus + select the field's text
 *   {action: 'blur',            selector}
 *
 * ── Visibility / animations (all accept optional `duration` in ms, default 400) ──
 *   {action: 'show',     selector, duration}
 *   {action: 'hide',     selector, duration}
 *   {action: 'fadeIn',   selector, duration}
 *   {action: 'fadeOut',  selector, duration}
 *   {action: 'slideIn',  selector, duration}    .slideDown()
 *   {action: 'slideOut', selector, duration}    .slideUp()
 *
 * ── UI ──
 *   {action: 'toast',     message, type, duration}   types: success|error|warning|info
 *                                                    alias: createToast
 *   {action: 'showModal', title, content, id, closeButton}    alias: modal
 *   {action: 'hideModal', id}                                 alias: closeModal
 *                                                             (no id ⇒ close all)
 *
 * ── Navigation / history ──
 *   {action: 'pushHistory',    path, handler, data, title}
 *   {action: 'replaceHistory', path, handler, data, title}
 *   {action: 'back'}
 *   {action: 'forward'}
 *   {action: 'redirect',  url}
 *   {action: 'refresh'}                                 reloads the page
 *   {action: 'load',      target|selector, url, fragment, showLoading, onError, onSuccess}
 *
 * ── Programmatic event trigger ──
 *   {action: 'trigger', selector, event, delay}    alias: click
 *
 * ── Audio / voice (browsers gate first audio behind a user gesture) ──
 *   {action: 'playSound',  url, volume, loop}
 *   {action: 'stopSounds'}
 *   {action: 'speak',      text, lang, rate, pitch, volume}
 *
 * ── Muffin scope (contextual addressing system) ──
 *   {action: 'attachMuffin', selector, muffin}
 *   {action: 'detachMuffin', selector}
 *
 * ── Escape hatches (use sparingly — prefer adding a typed verb) ──
 *   {action: 'runScript',  script}    Eval JS string; $ (jQuery) in scope
 *   {action: 'consoleLog', log}       Server-driven console output
 *
 * ============================================================================
 * TRIGGER VOCABULARY  (data-trigger="..." values on .treact elements)
 * ============================================================================
 *
 *   click                        click event
 *   hover[:debounceMs]           mouseenter (with optional enter delay)
 *   blur                         focus loss
 *   change                       value change (select, checkbox, radio, input)
 *   keyUp[:debounceMs]           key release (with optional debounce)
 *   keyDown[:debounceMs]         key press  (with optional debounce)
 *   enterKey                     Enter pressed in input
 *   load                         fires once on element init (auto from MutationObserver)
 *   viewportOnce                 fires once when element first enters viewport
 *   viewportMany                 fires every time element enters viewport
 *   refreshEvery:ms              polls every N ms unconditionally
 *   refreshEveryVisible:ms       polls every N ms, gated by element in viewport
 *   refreshEveryTabactive:ms     polls every N ms, gated by tab not backgrounded
 *   drop                         HTML5 drop target (paired with .treact-drag sources)
 *
 * Combinable via comma: data-trigger="load,refreshEveryVisible:3000"
 *
 * ============================================================================
 * HTML CONVENTIONS
 * ============================================================================
 *
 *   class="treact"                Marks an element as triggerable / treact-managed.
 *   class="treact-drag"           Marks an element as draggable (drag source).
 *   class="treact-drop"           Auto-added by framework to drop zones.
 *   data-handler="name"           Which server-side handler to invoke.
 *                                 Slash-namespaced supported: "cps/start".
 *   data-trigger="..."            Which client event(s) trigger the handler.
 *   data-collect="sel,sel"        Also send these other elements' state in the request.
 *   data-muffin="scope"           Context scope (read as ancestor tree on every send).
 *   data-treact-sse="url"         Open an EventSource to this URL.
 *   data-treact-initialized       Auto-set by framework (do not write manually).
 *
 *   Data-attribute keys arrive in PHP as $data['data_<name>'], with hyphens in
 *   the attribute converted to underscores in the key. So data-line-id="42"
 *   becomes $data['data_line_id'] = "42".
 *
 * ============================================================================
 * DEBUG MODE
 * ============================================================================
 *
 *   Enable per-page:    visit any URL with ?debug=1 appended
 *   Enable from code:   Treact.debug(true)
 *   Manual hygiene scan: Treact.checkMistakes()
 *
 *   Debug mode loads treact.checkmistakes.js which warns about:
 *     - Duplicate IDs (silent disasters in an ID-addressed framework)
 *     - IDs starting with '#' (confused CSS-selector syntax in attributes)
 *     - Classes starting with '.' (same mistake on the class attribute)
 *   It also makes debug() inside the framework log to console.
 *
 * ============================================================================
 */


(function($) {
    'use strict';

    // Configuration
    const CONFIG = {
        DEBUG_MODE: false,
        HANDLER_URL: 'handler.php',
        SELECTOR: '.treact',
        VIEWPORT_THRESHOLD: 0.1,
        DEFAULT_REFRESH_INTERVAL: 5000,
        ajaxUrl: 'handler.php',
        baseUrl: '',
        historyParam: 'path',
        CHECK_MISTAKES_URL: './treact.checkmistakes.js'
    };

    // Convenience: any page can be checked by appending ?debug=1 to its URL.
    // Sets DEBUG_MODE before doc-ready, which causes the checkmistakes linter
    // to be loaded and run automatically below.
    if (new URLSearchParams(window.location.search).get('debug') === '1') {
        CONFIG.DEBUG_MODE = true;
    }

    let processingHistory = false; // Prevent duplicate history triggers
    let mutationObserver = null;
    let treactDebounce = '';
    const treactActiveSounds = [];

    // Active intervals and observers storage
    const activeIntervals = new Map();
    const intersectionObserver = createIntersectionObserver();

    initializePopStateHandler();

    // Track elements currently being processed to prevent double-triggers
    const processingElements = new WeakSet();

    /**
     * Internal debug logger. Gates output on CONFIG.DEBUG_MODE so the
     * framework stays silent in production by default. Enable with
     * `?debug=1` in the URL or `Treact.debug(true)` from the console.
     *
     * @param {...*} args - Anything you'd pass to console.log
     * @returns {void}
     */
    function debug(...args) {
        if (CONFIG.DEBUG_MODE) {
            console.log('[Treact]', ...args);
        }
    }

    /**
     * Lazily load `treact.checkmistakes.js` (the DOM-hygiene linter) and run
     * its `checkMistakes()` function. After the first load the script stays
     * cached and subsequent calls just re-invoke `window.checkMistakes()`.
     *
     * Surfaces silent bugs that an ID-addressed framework is uniquely exposed to:
     *   - Duplicate IDs (jQuery picks the first; the rest are invisible)
     *   - IDs that mistakenly start with '#' (confused selector syntax)
     *   - Classes that mistakenly start with '.' (same mistake on class attr)
     *
     * @returns {void}
     */
    function runCheckMistakes() {
        if (typeof window.checkMistakes === 'function') {
            window.checkMistakes();
            return;
        }
        $.getScript(CONFIG.CHECK_MISTAKES_URL)
            .done(function() {
                if (typeof window.checkMistakes === 'function') {
                    window.checkMistakes();
                } else {
                    debug('checkMistakes loaded but function not found');
                }
            })
            .fail(function(_, status, err) {
                debug('Failed to load checkMistakes:', status, err);
            });
    }

    /**
     * Parse a comma-separated `data-trigger` string into an array of triggers.
     * Each entry may carry a parameter after a colon (e.g. "keyUp:300").
     *
     * @param {string} triggerString - Raw value of the data-trigger attribute
     * @returns {string[]} Array of trimmed trigger tokens; empty array if input falsy
     *
     * @example
     *   parseTriggers("load,refreshEveryVisible:3000")
     *   // → ["load", "refreshEveryVisible:3000"]
     */
    function parseTriggers(triggerString) {
        if (!triggerString) return [];
        return triggerString.split(',').map(t => t.trim());
    }

    /**
     * Inspect an element and return the appropriate "value" payload for it.
     *
     * For form inputs the value depends on type — checkboxes/radios need
     * `checked` separate from `value`, selects need `selectedText` for display,
     * file inputs and contenteditable elements have their own shape. This
     * function returns a small object that downstream code merges into the
     * outgoing AJAX payload so the server gets all the relevant state.
     *
     * @param {jQuery} $element - The element being inspected
     * @returns {Object} A shape that varies by element type:
     *   - checkbox/radio: {value, checked, state}
     *   - select        : {value, selectedIndex, selectedText [or selectedTexts]}
     *   - textarea      : {value, length}
     *   - input         : {value, length}
     *   - contenteditable: {value, html, length}
     *   - anything else : {value, text, html}
     */
    function getElementValue($element) {
        const tagName = ($element.prop('tagName') || '').toLowerCase();
        const type = ($element.attr('type') || '').toLowerCase();

        // Checkbox
        if (type === 'checkbox') {
            return {
                value: $element.val(),
                checked: $element.prop('checked'),
                state: $element.prop('checked') ? 'checked' : 'unchecked'
            };
        }

        // Radio button
        if (type === 'radio') {
            return {
                value: $element.val(),
                checked: $element.prop('checked'),
                state: $element.prop('checked') ? 'checked' : 'unchecked'
            };
        }

        // Select (single)
        if (tagName === 'select' && !$element.prop('multiple')) {
            return {
                value: $element.val(),
                selectedIndex: $element.prop('selectedIndex'),
                selectedText: $element.find('option:selected').text()
            };
        }

        // Select (multiple)
        if (tagName === 'select' && $element.prop('multiple')) {
            return {
                value: $element.val(), // Array of selected values
                selectedCount: $element.find('option:selected').length,
                selectedTexts: $element.find('option:selected').map(function() {
                    return $(this).text();
                }).get()
            };
        }

        // Textarea
        if (tagName === 'textarea') {
            return {
                value: $element.val(),
                length: ($element.val() || '').length
            };
        }

        // Input text, password, email, etc.
        if (tagName === 'input') {
            return {
                value: $element.val(),
                length: ($element.val() || '').length
            };
        }

        // ContentEditable
        if ($element.attr('contenteditable') === 'true') {
            return {
                value: $element.text(),
                html: $element.html(),
                length: $element.text().length
            };
        }

        // Default (non-form elements: div, span, a, li, progress, custom
        // elements, …). jQuery .val() may return undefined, a number (an <li>'s
        // ordinal, <progress>/<meter>), or an array (<select multiple>) — none of
        // which have .trim(). Coerce defensively, then fall back to text.
        const rawVal = $element.val();
        const val = (typeof rawVal === 'string' ? rawVal : '').trim();
        const text = ($element.text() || '').trim();

        return {
            value: val || text || '',
            text: text,
            html: ($element.html() || '').trim()
        };
    }

    /**
     * Read a `data-*` attribute directly from the DOM and coerce the string
     * value into the closest JavaScript primitive.
     *
     * Bypasses jQuery's `.data()` cache (which is stale if the attribute was
     * mutated server-side after page load). Coercions, in priority order:
     *   - null/undefined  → returned as-is
     *   - trimmed empty   → ""
     *   - starts with {/[ and parses as JSON → parsed value
     *   - "true" / "false" → boolean
     *   - purely numeric  → parseFloat result
     *   - otherwise       → the trimmed string
     *
     * @param {jQuery} $element  - Element to read from
     * @param {string} attribute - Attribute name WITHOUT the "data-" prefix
     * @returns {*} Coerced value, null/undefined if absent
     */
    function getDataAttribute($element, attribute) {
        // Read directly from DOM attribute to avoid jQuery cache
        let attrValue = $element.attr('data-' + attribute);

        // Return null/undefined as-is
        if (attrValue === null || attrValue === undefined) {
            return attrValue;
        }

        // ALWAYS trim whitespace first (fixes HTML formatting issues)
        attrValue = attrValue.trim();

        // Empty string after trim
        if (attrValue === '') {
            return '';
        }

        // Try to parse as JSON if it looks like JSON
        if (attrValue.startsWith('{') || attrValue.startsWith('[')) {
            try {
                return JSON.parse(attrValue);
            } catch (e) {
                return attrValue;
            }
        }

        // Parse booleans BEFORE numbers (because "true"/"false" would pass !isNaN check in some cases)
        if (attrValue === 'true') return true;
        if (attrValue === 'false') return false;

        // Parse numbers (only if it's PURELY numeric)
        if (!isNaN(attrValue) && attrValue !== '') {
            return parseFloat(attrValue);
        }

        // Return as string
        return attrValue;
    }

    /**
     * Return all `data-*` attributes of an element as a coerced object.
     *
     * Iterates the raw DOM attribute list (not jQuery's data cache) so that
     * any server-side mutation of attributes is reflected. Each value is
     * coerced via getDataAttribute (JSON, boolean, number, or string).
     * Keys retain their kebab-case form as written in HTML; conversion to
     * snake_case for the PHP payload happens later in collectElementData.
     *
     * @param {jQuery} $element - Element to inspect
     * @returns {Object<string, *>} Object keyed by attribute name (minus "data-")
     */
    function getAllDataAttributes($element) {
        const data = {};
        const attributes = $element[0].attributes;

        for (let i = 0; i < attributes.length; i++) {
            const attr = attributes[i];
            if (attr.name.startsWith('data-')) {
                const key = attr.name.substring(5); // Remove 'data-' prefix
                data[key] = getDataAttribute($element, key);
            }

        }

        return data;
    }


    /**
     * Gather a comprehensive snapshot of an element's state for sending to the
     * server. This is the function that builds the `data` payload of every
     * AJAX request — what handlers receive as PHP's $data.
     *
     * Includes: identity (id, name, class, tagName, type), all data-*
     * attributes (with hyphens converted to underscores in the key), value
     * and state (per getElementValue), form properties (disabled, readonly,
     * required, placeholder), position/dimensions, viewport state, the closest
     * data-muffin scope, the full muffin ancestor tree, file references for
     * uploads, and a timestamp.
     *
     * Special keys produced:
     *   data_<key>          — for each data-* attribute (hyphens → underscores)
     *   data_collectedItems — populated by sendToServer when data-collect is set
     *   muffin              — value of the closest [data-muffin] ancestor
     *   tree                — array of muffin values from root to leaf
     *   hasFiles / _fileElements — when a file input has files selected
     *
     * @param {jQuery} $element - Element to introspect
     * @returns {Object} Payload object ready to JSON-stringify as request data
     */
    function collectElementData($element) {
        // Get all data attributes directly from DOM (not jQuery cache!)
        const dataAttributes = getAllDataAttributes($element);

        let tree = collectTreactTree($element);

        let data = {
            // Element identification
            id: $element.attr('id') || '',
            name: $element.attr('name') || '',
            className: $element.attr('class') || '',
            classes: $element.attr('class') || '',
            tagName: ($element.prop("tagName") || '').toLowerCase(),
            type: $element.attr('type') || '',
            tree: tree || [],

            // Handler and trigger (read from DOM, not cache)
            data_handler: getDataAttribute($element, 'handler') || '',
            data_trigger: getDataAttribute($element, 'trigger') || '',
            contentEditable: $element.attr('contenteditable') || false,

            // All data attributes (from DOM attributes, not jQuery .data()).
            // Hyphens in the attribute name (data-line-id) are converted to
            // underscores in the payload key (data_line_id) so PHP handlers can
            // read them as natural snake_case keys. This matches the convention
            // every other modern framework uses (JS's own dataset normalises
            // data-line-id to dataset.lineId via kebab→camelCase).
            ...Object.keys(dataAttributes).reduce((acc, key) => {
                const normalized = 'data_' + key.replace(/-/g, '_');
                acc[normalized] = dataAttributes[key];
                return acc;
            }, {}),

            // Value and state (robust detection)
            ...getElementValue($element),

            // Form-specific attributes
            disabled: $element.prop('disabled') || false,
            readonly: $element.prop('readonly') || false,
            required: $element.prop('required') || false,
            placeholder: $element.attr('placeholder') || '',

            // Position and dimensions
            visible: $element.is(':visible'),
            offsetTop: ($element.offset() || {}).top || 0,
            offsetLeft: ($element.offset() || {}).left || 0,
            width: $element.width(),
            height: $element.height(),

            // Viewport state
            inViewport: $element.data('in-viewport') || false,

            // Timestamp
            timestamp: Date.now()
        };

        // Gestion des 🧁 MUFFINS
        //~ const $muffinElement = $element.closest('[data-muffin]').attr;
        //~ const muffinElement = target.closest('[data-muffin]');
        //~ const muffinScope = $muffinElement ? $($muffinElement).attr('data-') : null;
        //~ data.muffinScope = muffinScope || '';
        let muffin = $element.closest('[data-muffin]').attr('data-muffin');
        data.muffin = muffin;


        // Si on a des fichiers
        if ($element.is('input[type="file"]') && $element[0].files.length > 0) {
            data.hasFiles = true;
            data.fileCount = $element[0].files.length;
            data._fileElements = $element[0].files; // Store reference
        }

        //~ console.log(data);
        return data;
    }

    /**
     * Resolve a `data-collect` selector (or comma-separated list of selectors)
     * into a numeric-indexed object of element payloads.
     *
     * Each matched element goes through collectElementData. The resulting object
     * is attached to the request as `data.data_collectedItems` so the server can
     * read related-element state via `$data['data_collectedItems'][0]['value']`
     * etc. This is the mechanism that powers cascading selects, multi-field
     * form submission, confirm-password validation, and any other "this handler
     * depends on these other elements" pattern.
     *
     * @param {string} selectorString - One or more jQuery selectors, comma-separated
     * @returns {Object<number, Object>} Numeric-keyed map of element payloads,
     *                                   empty object if no selector or no matches
     */
    function collectMultipleElements(selectorString) {
        if (!selectorString || selectorString.trim() === '') {
            return {};
        }

        const collectedItems = {};
        let itemIndex = 0;

        // Split by comma for multiple selectors
        const selectors = selectorString.split(',').map(s => s.trim());

        selectors.forEach(selector => {
            const $elements = $(selector);

            if ($elements.length === 0) {
                debug('Warning: No elements found for selector:', selector);
                return;
            }

            debug(`Collecting ${$elements.length} element(s) for selector: ${selector}`);

            $elements.each(function() {
                const $el = $(this);


                // Use our existing collectElementData function!
                collectedItems[itemIndex] = collectElementData($el);

                itemIndex++;
            });
        });

        debug(`Total collected items: ${itemIndex}`, collectedItems);

        return collectedItems;
    }



    /**
     * Walk up the DOM tree from `element` to `<body>` and collect every
     * `data-muffin` value encountered. Returned root-first (outermost first).
     *
     * This produces the "muffin tree" — treact's contextual addressing system.
     * A click inside a list item inside a panel inside a dashboard gives the
     * server the full structural path without any of those layers needing IDs.
     *
     * Accepts either a jQuery wrapper or a raw DOM element. Hard cap of 100
     * iterations as a safety against malformed DOM with circular references.
     *
     * @param {jQuery|Element} element - Starting point of the upward walk
     * @returns {string[]} Muffin values, ordered root → leaf; empty if none
     */
    function collectTreactTree(element) {
        // Handle jQuery objects
        if (element instanceof jQuery) {
            element = element[0];
        }

        // Validate element
        if (!element || !element.nodeType) {
            console.error('❌ Invalid element passed to collectMuffinTree:', element);
            return [];
        }

        // Check if element is in DOM
        if (!document.body.contains(element)) {
            console.warn('⚠️ Element is not in DOM:', element);
        }

        const tree = [];
        let current = element;
        let safety = 0; // Prevent infinite loops
        const MAX_ITERATIONS = 100;

        while (current && safety < MAX_ITERATIONS) {
            safety++;

            debug(`collectTreactTree iter ${safety}:`, current.tagName, current.getAttribute('data-muffin'));

            const muffin = current.getAttribute('data-muffin');
            if (muffin) {
                tree.unshift(muffin);
            }

            // Stop at body
            if (current === document.body) {
                break;
            }

            // Try parentElement, fallback to parentNode
            current = current.parentElement || current.parentNode;

            // Safety check
            if (current && current.nodeType !== 1) {
                console.warn('⚠️ Hit non-element node:', current);
                break;
            }
        }

        if (safety >= MAX_ITERATIONS) {
            console.error('❌ collectMuffinTree exceeded max iterations!');
        }

        return tree;
    }

    /**
     * Wire up event listeners on a single `.treact` element based on its
     * `data-trigger` attribute. This is the function that *makes* triggers fire.
     *
     * Reads `data-trigger` (comma-separated), parses each token, and for each
     * one binds the corresponding native DOM event handler (click, blur, change,
     * etc.) or sets up an IntersectionObserver / setInterval (viewport / refresh
     * triggers). Marks the element with `data-treact-initialized` to prevent
     * double-binding when the MutationObserver re-scans.
     *
     * Hover, keyUp, keyDown accept an optional debounce after a colon
     * (e.g. "keyUp:300" → 300ms debounce).
     *
     * Side effects:
     *   - Records original value as `data-originalvalue` for revert features.
     *   - For load trigger, fires sendToServer immediately.
     *   - For viewport triggers, calls intersectionObserver.observe().
     *   - For refresh triggers, calls setupIntervals (registers interval).
     *
     * @param {jQuery} $element - The .treact element to initialize
     * @returns {void}
     */
    function initializeSingleElement($element) {
        // Skip if already initialized
        if ($element.data('treact-initialized')) {
            debug('Element already initialized, skipping:', $element[0]);
            return;
        }

        debug('Initializing element:', $element[0]);

        // Mark as initialized FIRST to prevent double-init
        $element.data('treact-initialized', true);

        // Store original value if not already stored
        const value = $element.val() || $element.text();
        if (value && !getDataAttribute($element, 'originalvalue')) {
            $element.attr('data-originalvalue', value);
        }

        // Parse triggers (read from DOM, not cache!)
        const triggerString = getDataAttribute($element, 'trigger') || '';
        const triggers = parseTriggers(triggerString);

        debug('Element triggers:', triggers);

        // Setup event handlers
        triggers.forEach(trigger => {
            const baseTrigger = trigger.split(':')[0];
            const delayTrigger = trigger.split(':')[1] || 0; // Peut être un délai ou un debounce



            switch (baseTrigger) {

                case 'hover': {
                    // Per-element state: data('treact-hover-timer') holds the pending enter
                    // timeout, data('treact-hover-active') is true between enter and leave.
                    // Storing on the element (not module scope) means hovered elements don't
                    // race against each other.
                    var namespace = 'treact.hover';
                    let handlerEnter = $element.attr('data-handler-enter') || $element.attr('data-handler');
                    let handlerLeave = $element.attr('data-handler-leave') || $element.attr('data-handler');

                    $element.on('mouseenter.' + namespace, function(e) {
                        $element.attr('data-triggeredby', baseTrigger);
                        const timer = setTimeout(function() {
                            $element.data('treact-hover-active', true);
                            const originalHandler = $element.attr('data-handler');
                            $element.attr('data-handler', handlerEnter);
                            sendToServer($element, {
                                hoverState: 'enter',
                                clientX: e.clientX,
                                clientY: e.clientY
                            });
                            $element.attr('data-handler', originalHandler);
                        }, delayTrigger);
                        $element.data('treact-hover-timer', timer);
                    });

                    $element.on('mouseleave.' + namespace, function(e) {
                        clearTimeout($element.data('treact-hover-timer'));
                        if ($element.data('treact-hover-active')) {
                            const originalHandler = $element.attr('data-handler');
                            $element.attr('data-handler', handlerLeave);
                            sendToServer($element, {
                                hoverState: 'leave'
                            });
                            $element.attr('data-handler', originalHandler);
                            $element.data('treact-hover-active', false);
                        }
                    });

                    break;
                }

                case 'click':
                    $element.off('click.treact').on('click.treact', function(e) {
                        $element.attr('data-triggeredby', baseTrigger);
                        $element.attr('data-triggeredbydetails', JSON.stringify({
                            shiftKey: e.shiftKey,
                            ctrlKey: e.ctrlKey,
                            altKey: e.altKey
                        }));
                        e.preventDefault();
                        e.stopPropagation();
                        debug('Click triggered on:', this);
                        sendToServer($element);
                    });
                    break;

                case 'blur':
                    $element.attr('data-triggeredby', baseTrigger);
                    $element.off('blur.treact').on('blur.treact', function() {
                        debug('Blur triggered on:', this);
                        sendToServer($element);
                    });
                    break;

                case 'change':
                    $element.off('change.treact').on('change.treact', function() {
                        debug('Change triggered on:', this);
                        sendToServer($element);
                    });
                    break;

                case 'keyUp':
                case 'keyup':
                    $element.off('keyup.treact').on('keyup.treact', function(e) {
                        if (e.key != 'Shift' && e.key != 'Control' && e.key != 'Alt' && e.key != 'ArrowUp' && e.key != 'ArrowDown' && e.key != 'Escape') {
                            $element.attr('data-triggeredby', baseTrigger);
                            $element.attr('data-triggeredbydetail', e.key);
                            if (delayTrigger == 0) {
                                debug('Keyup triggered on:', this);
                                sendToServer($element);
                            } else {
                                clearTimeout(treactDebounce);
                                treactDebounce = setTimeout(function() {
                                    debug('Keyup triggered after debounce ' + delayTrigger + 'ms on:', this);
                                    sendToServer($element)
                                }, delayTrigger);
                            }
                        }
                    });
                    break;

                case 'keyDown':
                case 'keydown':
                    $element.off('keydown.treact').on('keydown.treact', function(e) {
                        if (e.key != 'Shift' && e.key != 'Control' && e.key != 'Alt') {
                            $element.attr('data-triggeredby', baseTrigger);
                            $element.attr('data-triggeredbydetail', e.key);
                            if (delayTrigger == 0) {
                                debug('Keydown triggered on:', this);
                                sendToServer($element);
                            } else {
                                clearTimeout(treactDebounce);
                                treactDebounce = setTimeout(function() {
                                    debug('Keydown triggered after debounce ' + delayTrigger + 'ms on:', this);
                                    sendToServer($element)
                                }, delayTrigger);
                            }
                        }
                    });
                    break;

                case 'enterkey':
                case 'enterKey':
                    $element.off('keypress.treact.enter').on('keypress.treact.enter', function(e) {
                        if (e.which === 13) {
                            $element.attr('data-triggeredby', baseTrigger);
                            e.preventDefault();
                            debug('Enter key triggered on:', this);
                            sendToServer($element);
                        }
                    });
                    break;

                case 'load':
                    $element.attr('data-triggeredby', 'load');
                    debug('Load trigger - sending immediately');
                    sendToServer($element);
                    break;

                case 'viewportOnce':
                    $element.attr('data-triggeredby', 'viewportOnce');
                    intersectionObserver.observe($element[0]);
                    break;

                case 'viewportMany':
                    $element.attr('data-triggeredby', 'viewportMany');
                    intersectionObserver.observe($element[0]);
                    break;

                case 'drop':
                    $element.addClass('treact-drop'); // Mark as drop zone

                    $element.on('dragover', function(e) {
                        e.preventDefault();
                        e.originalEvent.dataTransfer.dropEffect = 'copy';
                        $element.addClass('drag-over');
                    });

                    $element.on('dragleave', function(e) {
                        $element.removeClass('drag-over');
                    });

                    $element.on('drop', function(e) {
                        e.preventDefault();
                        $element.removeClass('drag-over');

                        // 🎯 Get dragged data and inject it into the element!
                        try {
                            var dragData = JSON.parse(e.originalEvent.dataTransfer.getData('application/json'));

                            debug('drop payload:', dragData);

                            // Temporarily attach the dragged payload as real DOM attributes
                            // so collectElementData picks them up. Must use .attr(), not
                            // .data() — .data() stores in jQuery's cache and never reaches
                            // the DOM, so getAllDataAttributes wouldn't see it.
                            applyDragPayload($element, dragData);
                            $element.attr('data-triggeredby', baseTrigger);
                            // 🚀 Now trigger the REGULAR Treact flow!
                            sendToServer($element);

                        } catch (err) {
                            console.error('Invalid drag data:', err);
                        }
                    });

            }
        });

        // Setup periodic intervals
        setupIntervals($element, triggers);
    }

    /**
     * Set up `setInterval` timers for any `refreshEvery*` triggers on an
     * element. Three variants are supported:
     *
     *   refreshEvery:N             — fires every N ms unconditionally
     *   refreshEveryVisible:N      — fires every N ms, only if element in viewport
     *                                (registers the element with intersectionObserver
     *                                so the `in-viewport` flag gets maintained)
     *   refreshEveryTabactive:N    — fires every N ms, only if document.hidden is false
     *
     * Idempotent per element: replaces any existing interval for the same
     * element-key (id or generated treact-key) so triggers can be safely
     * re-initialized without leaking timers.
     *
     * @param {jQuery}   $element - Element to attach interval(s) to
     * @param {string[]} triggers - Output of parseTriggers — only refreshEvery* entries are acted on
     * @returns {void}
     */
    function setupIntervals($element, triggers) {
        const elementKey = $element.attr('id') || 'treact-' + Date.now() + '-' + Math.random();

        triggers.forEach(trigger => {
            let intervalTime = null;
            let shouldRun = () => true;

            if (trigger.startsWith('refreshEvery')) {
                // Parse refreshEvery:5000, refreshEveryVisible:3000, refreshEveryTabactive:10000
                const parts = trigger.split(':');
                const triggerType = parts[0];
                intervalTime = parseInt(parts[1]) || CONFIG.DEFAULT_REFRESH_INTERVAL;

                if (triggerType === 'refreshEveryVisible') {
                    shouldRun = () => $element.data('in-viewport') === true;
                    // The in-viewport flag is only updated when the
                    // IntersectionObserver is watching this element. Without
                    // this observe() call shouldRun() would return undefined
                    // forever and the interval would never fire sendToServer.
                    // observe() is idempotent — safe to call even if the element
                    // is also a viewportOnce/viewportMany trigger.
                    intersectionObserver.observe($element[0]);
                } else if (triggerType === 'refreshEveryTabactive') {
                    shouldRun = () => !document.hidden;
                }

                // Clear existing interval if any
                if (activeIntervals.has(elementKey)) {
                    clearInterval(activeIntervals.get(elementKey));
                }

                // Set new interval
                const intervalId = setInterval(() => {
                    if (shouldRun()) {
                        debug('Periodic trigger for:', elementKey, trigger);
                        sendToServer($element);
                    }
                }, intervalTime);

                activeIntervals.set(elementKey, intervalId);
                debug('Set interval for:', elementKey, trigger, intervalTime + 'ms');
            }
        });
    }

    /**
     * Initialize a single treact element
     */

    /**
     * Send element state to the server and apply the resulting action stream.
     * This is the heart of the framework — every triggered interaction flows
     * through here.
     *
     * Pipeline:
     *   1. Collect element state via collectElementData (+ extra payload).
     *   2. Resolve any data-collect selectors via collectMultipleElements.
     *   3. Build the request body: JSON for normal requests, multipart/form-data
     *      when a file input is involved (auto-detected from data.hasFiles).
     *   4. POST to CONFIG.HANDLER_URL.
     *   5. Parse the response line-by-line as it arrives (xhrFields.onprogress)
     *      so server-side tFlush() can stream actions progressively.
     *   6. Apply any remaining unprocessed lines in .done().
     *
     * Per-element deduplication: while a request from this element is in flight,
     * subsequent triggers on the same element are silently skipped via the
     * processingElements WeakSet. Prevents double-fires from rapid clicks.
     *
     * @param {jQuery} $element - Element whose state to collect and post
     * @param {Object} [extra]  - Extra fields merged into the collected data.
     *                            Used by hover (hoverState, clientX, clientY)
     *                            and mobile drop (fake event details).
     * @returns {void}
     *
     * @example
     *   sendToServer($('#save-btn'));
     *   sendToServer($card, { hoverState: 'enter', clientX: e.clientX });
     */
    function sendToServer($element, extra) {
        // Prevent double-triggering while processing
        if (processingElements.has($element[0])) {
            debug('Element already being processed, skipping', $element[0]);
            return;
        }

        processingElements.add($element[0]);

        // On récupère les données de l'élément cliqué principal
        const data = collectElementData($element);
        if (extra && typeof extra === 'object') {
            Object.assign(data, extra);
        }

        // Si l'élément cliqué a des fichiers :
        var hasFiles = data.hasFiles || false;


        // On récupère le handler principal
        let handler = data.data_handler;

        let windowdetails = {
            width: $(window).width(),
            height: $(window).height()
        };

        let uri = window.location || '';

        // On collectione les éléments collect si notre élément principal a un attribut data-collect="... selecteurs";
        const collectSelectors = $element.attr('data-collect');
        if (collectSelectors) { // Si il a bien l'attribut
            debug('Collecting additional elements with selectors:', collectSelectors);
            // on ajoute la collection d'éléments à notre objet data grâce à notre collecteur multipe
            data.data_collectedItems = collectMultipleElements(collectSelectors);
        }

        //~ $(data.data_collectedItems).each(function(item,data) {

        //~ console.log("ouiiii",item,data);
        //~ });


        // Si l'élément principal n'avais pas de fichier, on vérifie dans les éléments collectés
        // S'il y en a un on met le drapeau hasFiles à true
        if (!hasFiles && collectSelectors) {
            $.each(data.data_collectedItems, function(index, item) {
                if (item.hasFiles === true) {
                    hasFiles = true;
                    return false; // break the loop in jQuery
                }
            });
        }

        // Données "normales" (hors fichiers) : handler et data (contient data et data.data_collectedItems);

        var requestData = {
            uri: uri,
            window: windowdetails,
            handler: handler,
            data: data
        };

        debug("Envoyé:", requestData);



        // Build the request body up-front so ajaxOptions has the final values
        // at construction time (no var-hoisting trap, no later mutation).
        var requestBody;
        var requestContentType;

        if (hasFiles) {
            var formData = new FormData();
            formData.append('treact_data', JSON.stringify(requestData));

            // Files from the trigger element
            if (data._fileElements) {
                for (var i = 0; i < data._fileElements.length; i++) {
                    formData.append('treact_file_' + (data.name || 'file') + '[]', data._fileElements[i]);
                }
            }

            // Files from collected elements
            for (var key in data.data_collectedItems) {
                if (data.data_collectedItems[key]._fileElements) {
                    var collectedFiles = data.data_collectedItems[key]._fileElements;
                    var fieldName = data.data_collectedItems[key].name || 'file_' + key;
                    for (var j = 0; j < collectedFiles.length; j++) {
                        formData.append('treact_file_' + fieldName + '[]', collectedFiles[j]);
                    }
                }
            }

            requestBody = formData;
            requestContentType = false; // let the browser set the multipart boundary
            debug('📤 Uploading files:', formData);
        } else {
            requestBody = JSON.stringify(requestData);
            requestContentType = 'application/json';
            debug('🎯 Treact Request:', handler, requestData);
        }

        // Cosmetic URL tag for DevTools — guard against literal "undefined" when
        // the element has no data-handler/data-action.
        var urlTag = data.data_handler ?
            '?(' + data.data_handler + (data.data_action ? ':' + data.data_action : '') + ')' :
            '';

        var ajaxOptions = {
            url: CONFIG.HANDLER_URL + urlTag,
            method: 'POST',
            dataType: 'text',
            cache: false,
            data: requestBody,
            contentType: requestContentType,
            processData: !hasFiles,
            complete: function() {
                processingElements.delete($element[0]);
            }
        };

        var receivedData = '';
        // Count of non-empty response lines that onprogress has already executed.
        // .done() uses this to process only what onprogress hasn't, avoiding the
        // double-execute bug on terminated responses.
        var linesProcessed = 0;

        ajaxOptions.xhr = function() {
            var xhr = new window.XMLHttpRequest();

            // Listen to progressive data
            xhr.addEventListener("progress", function(evt) {
                if (evt.lengthComputable) {
                    var percentComplete = evt.loaded / evt.total;
                    debug('📊 Upload progress:', Math.round(percentComplete * 100) + '%');
                }
            });

            return xhr;
        };

        ajaxOptions.xhrFields = {
            onprogress: function(e) {
                // Initialize buffer on first call
                if (!this.incompleteBuffer) {
                    this.incompleteBuffer = '';
                }

                // Get new data chunk
                var response = e.currentTarget.response;
                var newData = response.substring(receivedData.length);
                receivedData = response;

                if (newData.trim()) {
                    // Add new data to buffer
                    this.incompleteBuffer += newData;

                    // Split by newlines (each tFlush() sends one terminated line)
                    var lines = this.incompleteBuffer.split('\n');

                    // Keep last line in buffer (might be incomplete)
                    this.incompleteBuffer = lines.pop() || '';

                    // Process only complete lines
                    lines.forEach(function(line) {
                        line = line.trim();
                        if (!line) return;

                        try {
                            var actions = JSON.parse(line);
                            debug('📦 Received progressive actions:', actions);
                            executeActions(actions);
                        } catch (e) {
                            debug('⚠️ Failed to parse progressive action:', e);
                        }
                        // Count attempted lines (success or failure) so .done()
                        // doesn't retry them.
                        linesProcessed++;
                    });
                }
            }
        };


        $.ajax(ajaxOptions).done(function(response) {
                debug('✅ Request completed');

                if (typeof response !== 'string' || !response.trim()) return;

                // Re-split the full response and skip the lines onprogress already
                // executed. Handles three cases at once:
                //   - terminated response, onprogress fired: nothing left to do
                //   - unterminated tail: the final line gets picked up here
                //   - small/fast response where onprogress never fired: we process all
                var remainingLines = response
                    .split(/\r?\n/)
                    .map(function(l) {
                        return l.trim();
                    })
                    .filter(function(l) {
                        return l.length > 0;
                    })
                    .slice(linesProcessed);

                remainingLines.forEach(function(line) {
                    try {
                        executeActions(JSON.parse(line));
                    } catch (e) {
                        debug('⚠️ Final response parse error:', e);
                    }
                });
            })
            .fail(function(xhr, status, error) {
                debug('❌ Request failed:', error);
                console.error('AJAX Error:', {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    responseText: xhr.responseText
                });
            });

    }

    /**
     * Schedule an array of actions for execution with cumulative delays.
     *
     * Each action's `delay` is RELATIVE to the previous action's scheduled time.
     * So [{action:'X'}, {action:'Y', delay:200}, {action:'Z', delay:300}] runs
     * X at 0ms, Y at 200ms, Z at 500ms.
     *
     * Each action runs via setTimeout at its computed offset, which means the
     * server can choreograph multi-step UI animations from a single response
     * without any client-side animation coordination code.
     *
     * @param {Action[]} actions - Action objects to execute, in order
     * @returns {void}
     */
    function executeActions(actions) {
        debug(`Executing ${actions.length} actions`);

        // Build action queue with delays
        let totalDelay = 0;
        const actionQueue = [];

        actions.forEach((action, index) => {
            const actionDelay = parseInt(action.delay) || 0;
            totalDelay += actionDelay;

            actionQueue.push({
                action: action,
                executeAt: totalDelay,
                index: index
            });

            debug(`Action ${index + 1} scheduled at ${totalDelay}ms:`, action);
        });

        // Execute actions with their delays
        actionQueue.forEach(item => {
            setTimeout(() => {
                executeSingleAction(item.action, item.index, actions.length);
            }, item.executeAt);
        });
    }

    /**
     * Dispatch a single action object to its executor.
     *
     * The big switch that defines the framework's runtime API. Reads
     * `action.action` (lowercased), routes to the appropriate executor function
     * (or inline handler for trivial verbs). Unknown action types are logged via
     * debug() and silently skipped — the framework does not throw, because a
     * single malformed action shouldn't break the entire response.
     *
     * See the ACTION VOCABULARY block at the top of this file for the full
     * list of verbs and their payload shapes. Aliases (e.g. setContent /
     * replaceElement / updateContent / html) all route to the same executor.
     *
     * @param {Action} action - The action object to execute
     * @param {number} index  - Position within the original actions array (for logging)
     * @param {number} total  - Total action count (for logging)
     * @returns {void}
     */
    function executeSingleAction(action, index, total) {
        debug(`Executing action ${index + 1}/${total}:`, action);

        const actionType = (action.action || '').toLowerCase();

        try {
            switch (actionType) {
                case 'load': {
                    const $loadTarget = $(action.target || action.selector);

                    if ($loadTarget.length === 0) {
                        debug('❌ Load target not found:', action.target || action.selector);
                        return;
                    }

                    if (!action.url) {
                        debug('❌ Load action missing URL');
                        return;
                    }

                    // action.url is the document; action.fragment optionally narrows to a sub-selector.
                    var loadUrl = action.url;
                    var fragmentSelector = action.fragment || '';
                    var fullUrl = fragmentSelector ? loadUrl + ' ' + fragmentSelector : loadUrl;

                    debug('📥 Loading fragment from:', fullUrl, 'into:', action.target || action.selector);

                    if (action.showLoading !== false) {
                        $loadTarget.addClass('treact-loading');
                    }

                    $loadTarget.load(fullUrl, function(response, status, xhr) {
                        $loadTarget.removeClass('treact-loading');

                        if (status === 'error') {
                            debug('❌ Load failed:', xhr.status, xhr.statusText);
                            if (action.onError) {
                                $loadTarget.html(action.onError);
                            }
                        } else {
                            debug('✓ Fragment loaded successfully');
                            if (action.onSuccess) {
                                var $callback = $(action.onSuccess);
                                if ($callback.length) {
                                    $callback.trigger('click');
                                }
                            }
                        }
                    });
                    break;
                }


                case 'click':
                case 'trigger': {
                    let $target = $(action.selector);
                    let eventName = action.event || 'click';

                    if ($target.length === 0) {
                        debug('❌ Trigger target not found:', action.target);
                        return;
                    }

                    debug('⚡ Virtual TRIGGER ' + eventName + ' on:', action.target);

                    var delay = action.delay || 0;

                    setTimeout(function() {
                        $target.trigger(eventName);
                        debug('✓ Event triggered:', eventName, $target[0]);
                    }, delay);

                    break;
                }

                case 'attachmuffin':
                    $(action.selector).attr('data-muffin', action.muffin);
                    debug('🧁 Muffin attached:', action.muffin, 'to', action.selector);
                    break;

                case 'detachmuffin':
                    $(action.selector).removeAttr('data-muffin');
                    debug('🧁 Muffin detached from', action.selector);
                    break;

                case 'insertafter':
                case 'appendafter':
                    $(action.selector).after(action.content);
                    break;

                case 'pushhistory':
                    executePushHistory(action);
                    break;

                case 'replacehistory':
                    executeReplaceHistory(action);
                    break;

                case 'back':
                    window.history.back();
                    break;

                case 'forward':
                    window.history.forward();
                    break;

                case 'addclass':
                    executeAddClass(action);
                    break;

                case 'removeclass':
                    executeRemoveClass(action);
                    break;

                case 'toggleclass':
                    executeToggleClass(action);
                    break;

                case 'css':
                case 'addcss':
                    $(action.selector).css(action.css);
                    debug('css:', action.selector, action.css);
                    break;

                case 'focus':
                    executeFocus(action);
                    break;
                case 'focusselect':
                    executeFocusSelect(action);
                    break;
                case 'blur':
                    executeBlur(action);
                    break;

                case 'html':
                case 'replaceelement':
                case 'replace':
                case 'replaceinner':
                case 'updatecontent':
                case 'setcontent':
                    executeSetContent(action);
                    break;

                case "replaceall":
                case "replaceouter":
                case "replacewith":
                    executeReplaceWith(action);
                    break;

                case 'setvalue':
                    executeSetValue(action);
                    break;

                case 'append':
                case 'appendcontent':
                    executeAppendContent(action);
                    break;

                case 'prepend':
                case 'prependcontent':
                    executePrependContent(action);
                    break;

                case 'removeelement':
                case 'remove':
                    executeRemove(action);
                    break;

                case 'attr':
                case 'setattribute':
                    executeSetAttribute(action);
                    break;

                case 'prop':
                case 'setprop':
                case 'setproperty':
                    $(action.selector).prop(action.prop, action.value);
                    break;

                case 'removeprop':
                case 'removeproperty':
                    $(action.selector).removeProp(action.prop);
                    break;

                case 'removeattr':
                case 'removeattribute':
                    executeRemoveAttribute(action);
                    break;

                case 'show':
                    executeShow(action);
                    break;

                case 'hide':
                    executeHide(action);
                    break;

                case 'fadein':
                    executeFadeIn(action);
                    break;

                case 'fadeout':
                    executeFadeOut(action);
                    break;

                case 'slidein':
                    executeSlideIn(action);
                    break;

                case 'slideout':
                    executeSlideOut(action);
                    break;

                case 'toast':
                case 'createtoast':
                    executeCreateToast(action);
                    break;

                case 'redirect':
                    executeRedirect(action);
                    break;

                case 'refresh':
                    executeRefresh(action);
                    break;

                case 'consolelog':
                    executeConsoleLog(action);
                    break;

                case 'modal':
                case 'showmodal':
                    executeShowModal(action);
                    break;

                case 'hidemodal':
                case 'closemodal':
                    executeHideModal(action);
                    break;

                case 'runscript':
                    debug(`runScript: ${action.script.substring(0, 50)}...`);
                    try {
                        (function($) {
                            eval(action.script);
                        })(jQuery);
                    } catch (e) {
                        console.error('Script error:', e);
                    }
                    break;

                case 'playsound':
                    if (action.url) {
                        playSound(
                            action.url,
                            action.volume !== undefined ? action.volume : 1.0,
                            action.loop || false
                        );
                    }
                    break;

                case 'stopsounds':
                    stopAllSounds();
                    break;

                case 'speak':
                    if (action.text) {
                        speakText(
                            action.text,
                            action.lang || 'en-US',
                            action.rate !== undefined ? action.rate : 1.0,
                            action.pitch !== undefined ? action.pitch : 1.0,
                            action.volume !== undefined ? action.volume : 1.0
                        );
                    }
                    break;
                default:
                    debug(`Unknown action type: ${actionType}`);
            }
        } catch (error) {
            debug(`Error executing action ${actionType}:`, error);
            console.error('[Treact Error]', error);
        }
    }


    /**
     * Push new history state
     * Adds a new entry to browser history
     */
    function executeAddClass(action) {
        const $elements = $(action.selector);
        debug(`addClass: Adding "${action.className}" to ${$elements.length} element(s) matching "${action.selector}"`);
        $elements.addClass(action.className);
    }

    function executeRemoveClass(action) {
        const $elements = $(action.selector);
        debug(`removeClass: Removing "${action.className}" from ${$elements.length} element(s) matching "${action.selector}"`);
        $elements.removeClass(action.className);
    }

    function executeToggleClass(action) {
        const $elements = $(action.selector);
        debug(`toggleClass: Toggling "${action.className}" on ${$elements.length} element(s) matching "${action.selector}"`);
        $elements.toggleClass(action.className);
    }

    function executeSetContent(action) {
        const $elements = $(action.selector);
        debug(`setContent: Setting content on ${$elements.length} element(s) matching "${action.selector}"`, action.content);
        $elements.html(action.content);
    }

    function executeReplaceWith(action) {
        const $elements = $(action.selector);
        debug(`replaceWith: Replacing ${$elements.length} element(s) matching "${action.selector}"`, action.content);
        $elements.replaceWith(action.content);
    }

    function executeSetValue(action) {
        const $elements = $(action.selector);
        debug(`setValue: Setting value on ${$elements.length} element(s) matching "${action.selector}"`, action.value);
        $elements.val(action.value);
    }

    function executeFocus(action) {
        debug(`focus: Focusing "${action.selector}"`);
        //~ alert("focus"+action.selector);
        $(action.selector).focus();
    }

    function executeFocusSelect(action) {
        debug(`focusSelect: Focusing & selecting text of "${action.selector}"`);
        // Focus, then select the field's text (quick-edit pattern). Call the
        // native methods per element so non-selectable matches are skipped
        // rather than throwing, and we don't rely on jQuery event-trigger timing.
        $(action.selector).each(function () {
            this.focus();
            if (typeof this.select === 'function') {
                this.select();
            }
        });
    }

    function executeBlur(action) {
        debug(`blur: Blurring "${action.selector}"`);
        $(action.selector).blur();
    }

    function executeAppendContent(action) {
        const $elements = $(action.selector);
        debug(`appendContent: Appending to ${$elements.length} element(s) matching "${action.selector}"`);
        $elements.append(action.content);
    }

    function executePrependContent(action) {
        const $elements = $(action.selector);
        debug(`prependContent: Prepending to ${$elements.length} element(s) matching "${action.selector}"`);
        $elements.prepend(action.content);
    }

    function executeRemove(action) {
        const $elements = $(action.selector);
        debug(`remove: Removing ${$elements.length} element(s) matching "${action.selector}"`);

        // Clean up before removal
        $elements.each(function() {
            cleanupElement($(this));
        });

        $elements.remove();
    }

    function executeSetAttribute(action) {
        const $elements = $(action.selector);
        debug(`setAttribute: Setting ${action.attribute}="${action.value}" on ${$elements.length} element(s) matching "${action.selector}"`);
        $elements.attr(action.attribute, action.value);
    }

    function executeRemoveAttribute(action) {
        const $elements = $(action.selector);
        debug(`removeAttribute: Removing "${action.attribute}" from ${$elements.length} element(s) matching "${action.selector}"`);
        $elements.removeAttr(action.attribute);
    }

    function executeShow(action) {
        const $elements = $(action.selector);
        const duration = parseInt(action.duration) || 0;
        debug(`show: Showing ${$elements.length} element(s) matching "${action.selector}" (duration: ${duration}ms)`);

        if (duration > 0) {
            $elements.fadeIn(duration);
        } else {
            $elements.show();
        }
    }

    function executeHide(action) {
        const $elements = $(action.selector);
        const duration = parseInt(action.duration) || 0;
        debug(`hide: Hiding ${$elements.length} element(s) matching "${action.selector}" (duration: ${duration}ms)`);

        if (duration > 0) {
            $elements.fadeOut(duration);
        } else {
            $elements.hide();
        }
    }

    function executeFadeIn(action) {
        const $elements = $(action.selector);
        const duration = parseInt(action.duration) || 400;
        debug(`fadeIn: Fading in ${$elements.length} element(s) matching "${action.selector}" (duration: ${duration}ms)`);
        $elements.fadeIn(duration);
    }

    function executeFadeOut(action) {
        const $elements = $(action.selector);
        const duration = parseInt(action.duration) || 400;
        debug(`fadeOut: Fading out ${$elements.length} element(s) matching "${action.selector}" (duration: ${duration}ms)`);
        $elements.fadeOut(duration);
    }

    function executeSlideIn(action) {
        const $elements = $(action.selector);
        const duration = parseInt(action.duration) || 400;
        debug(`slideIn: Sliding in ${$elements.length} element(s) matching "${action.selector}" (duration: ${duration}ms)`);
        $elements.slideDown(duration);
    }

    function executeSlideOut(action) {
        const $elements = $(action.selector);
        const duration = parseInt(action.duration) || 400;
        debug(`slideOut: Sliding out ${$elements.length} element(s) matching "${action.selector}" (duration: ${duration}ms)`);
        $elements.slideUp(duration);
    }

    /**
     * Show a modal dialog with a title, body content, and a close button.
     *
     * Appends a `<div class="treact-modal-overlay">` to `<body>`, fades it in,
     * then re-runs initializeTreact so any treact elements inside the modal
     * body (e.g. Cancel/Confirm buttons in a confirmation dialog) become
     * live triggers. Wires up three close paths: the × button, click-outside,
     * and the Escape key.
     *
     * Styling lives in treact.css (loaded as a sibling stylesheet).
     *
     * @param {Object} action
     * @param {string} [action.id]          - DOM id for the overlay; auto-generated if omitted
     * @param {string} [action.title]       - Header text
     * @param {string} [action.content]     - HTML body content (may contain treact elements)
     * @param {string|false} [action.closeButton="×"] - Custom close-button label, or false to hide
     * @returns {void}
     */
    function executeShowModal(action) {
        debug(`showModal: Showing modal with title "${action.title}"`);

        let modalId;
        //~ action.id = action.id || null;
        //~ console.log(action);
        if (action.id == undefined) {
            modalId = 'treact-modal-' + Date.now();
        } else {
            modalId = action.id;
        }

        //~ alert(modalId);

        // closeButton is dual-purpose: a boolean to toggle the default button, OR
        // a string to use as a custom label. true/undefined → default "×";
        // a non-empty string → that label; false → no close button at all.
        let closeBtn = action.closeButton;
        if (closeBtn === undefined || closeBtn === true) {
            closeBtn = '×';
        }

        // Build modal HTML
        const modalHtml = `
        <div id="${modalId}" class="treact-modal-overlay">
            <div class="treact-modal">
                <div class="gradient treact-modal-header">
                    <h3 class="treact-modal-title">${action.title || ''}</h3>
                    ${closeBtn ? `<button class="treact-modal-close" data-modal-id="${modalId}">${closeBtn}</button>` : ''}
                </div>
                <div class="treact-modal-body">
                    ${action.content || ''}
                </div>
            </div>
        </div>
    `;

        // Append to body
        $('body').append(modalHtml);

        // Lock background scroll while the modal is open
        lockBodyScroll();

        // Show with fade-in
        setTimeout(() => {
            $(`#${modalId}`).addClass('show');
        }, 10);

        // Re-initialize Treact for elements inside modal
        initializeTreact();

        // Close button handler
        $(`#${modalId} .treact-modal-close`).on('click', function() {
            executeHideModal({
                id: $(this).data('modal-id')
            });
        });

        // Click outside to close
        $(`#${modalId}`).on('click', function(e) {
            if ($(e.target).hasClass('treact-modal-overlay')) {
                executeHideModal({
                    id: modalId
                });
            }
        });

        // ESC key to close
        $(document).on('keydown.modal', function(e) {
            if (e.key === 'Escape') {
                executeHideModal({
                    id: modalId
                });
            }
        });
    }

    /**
     * Lock background page scroll while a modal is open. Idempotent — a second
     * modal won't double-apply — and compensates for the scrollbar's width so the
     * page doesn't shift sideways when it disappears. Previous inline styles are
     * stashed on <body> so the exact state is restored on unlock.
     *
     * @returns {void}
     */
    function lockBodyScroll() {
        const body = document.body;
        if (body.getAttribute('data-treact-scroll-locked') === '1') return; // already locked

        // The robust, iOS-safe technique: pin <body> at its current scroll offset
        // with position:fixed, so the page genuinely cannot scroll. The offset
        // keeps the content visually in place; scroll is restored on unlock.
        const scrollY = window.scrollY || window.pageYOffset || 0;
        const scrollbar = window.innerWidth - document.documentElement.clientWidth;

        body.setAttribute('data-treact-prev-style', body.getAttribute('style') || '');
        body.setAttribute('data-treact-scroll-y', String(scrollY));

        body.style.position = 'fixed';
        body.style.top = (-scrollY) + 'px';
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
        if (scrollbar > 0) body.style.paddingRight = scrollbar + 'px'; // avoid sideways shift

        body.setAttribute('data-treact-scroll-locked', '1');
    }

    /**
     * Restore background scroll — but only once no modal overlays remain (so
     * closing one of several stacked modals keeps the lock).
     *
     * @returns {void}
     */
    function unlockBodyScrollIfNoModals() {
        if ($('.treact-modal-overlay').length > 0) return; // another modal still open

        const body = document.body;
        if (body.getAttribute('data-treact-scroll-locked') !== '1') return;

        const scrollY = parseInt(body.getAttribute('data-treact-scroll-y') || '0', 10);
        const prev = body.getAttribute('data-treact-prev-style');

        // Restore the exact previous inline style, then the scroll position.
        if (prev) {
            body.setAttribute('style', prev);
        } else {
            body.removeAttribute('style');
        }
        body.removeAttribute('data-treact-scroll-locked');
        body.removeAttribute('data-treact-scroll-y');
        body.removeAttribute('data-treact-prev-style');

        window.scrollTo(0, scrollY);
    }

    /**
     * Hide a modal (or all open modals).
     *
     *   - With action.id  → close only that modal.
     *   - Without action.id → close every open `.treact-modal-overlay`.
     *
     * Removes the overlay element after a 300ms transition (matches treact.css
     * fade-out timing) and detaches the keydown.modal handler.
     *
     * @param {Object} action
     * @param {string} [action.id] - Specific modal id to close (omit to close all)
     * @returns {void}
     */
    function executeHideModal(action) {

        const modalId = action.id;

        if (modalId) {
            // Hide specific modal
            debug(`hideModal: Hiding modal ${modalId}`);
            $(`#${modalId}`).removeClass('show');
            setTimeout(() => {
                $(`#${modalId}`).remove();
                $(document).off('keydown.modal');
                unlockBodyScrollIfNoModals();
            }, 300); // Match CSS transition time
        } else {
            // Hide all modals
            debug(`hideModal: Hiding all modals`);
            $('.treact-modal-overlay').removeClass('show');
            setTimeout(() => {
                $('.treact-modal-overlay').remove();
                $(document).off('keydown.modal');
                unlockBodyScrollIfNoModals();
            }, 300);
        }
    }

    function executeRedirect(action) {
        debug(`redirect: Redirecting to "${action.url}"`);
        window.location.href = action.url;
    }

    function executeRefresh(action) {
        debug('refresh: Reloading page');
        window.location.reload();
    }

    function executeCreateToast(action) {
        debug('createToast:', action.message);

        const toast = $('<div class="treact-toast"></div>')
            .css({
                position: 'relative',
                bottom: '20px',
                padding: '15px 25px',
                marginTop: '20px',
                background: getToastColor(action.type),
                color: 'white',
                borderRadius: '5px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                zIndex: 10000,
                opacity: 0,
                fontSize: '14px',
                maxWidth: '100%'
            })
            .html(action.message);

        $('#toaster').append(toast);

        toast.animate({
            opacity: 1
        }, 300);

        setTimeout(() => {
            toast.animate({
                opacity: 0
            }, 300, function() {
                $(this).remove();
            });
        }, action.duration || 3000);
    }

    function getToastColor(type) {
        const colors = {
            success: '#28a745',
            error: '#dc3545',
            warning: '#ffc107',
            info: '#17a2b8'
        };
        return colors[type] || colors.info;
    }

    function executeConsoleLog(action) {
        console.log('[Treact Server Log]', action.log);
    }


    /**
     * Build the shared IntersectionObserver for viewport-triggered behaviour.
     *
     * On intersection (≥ CONFIG.VIEWPORT_THRESHOLD, default 10%):
     *   - Sets `data('in-viewport', true)` on the element (used by
     *     refreshEveryVisible's shouldRun gate).
     *   - For viewportOnce: fires sendToServer once, marks `viewport-triggered`.
     *   - For viewportMany: fires sendToServer every time.
     *
     * When the element scrolls out, sets `data('in-viewport', false)`.
     *
     * One observer is shared by all viewport-aware elements; individual
     * elements opt in via `intersectionObserver.observe()`.
     *
     * @returns {IntersectionObserver} The configured observer instance
     */
    function createIntersectionObserver() {
        return new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const $element = $(entry.target);
                const triggers = parseTriggers($element.data('trigger'));

                if (entry.isIntersecting) {
                    debug('Element entered viewport:', entry.target);

                    if (triggers.includes('viewportOnce') && !$element.data('viewport-triggered')) {
                        $element.data('viewport-triggered', true);
                        sendToServer($element);
                    }

                    if (triggers.includes('viewportMany')) {
                        sendToServer($element);
                    }

                    $element.data('in-viewport', true);
                } else {
                    $element.data('in-viewport', false);
                }
            });
        }, {
            threshold: CONFIG.VIEWPORT_THRESHOLD
        });
    }

    /**
     * Build a MutationObserver that watches the whole document body for
     * dynamically added treact-managed elements:
     *   - `.treact`          → event triggers, action targets
     *   - `[data-treact-sse]` → SSE subscribers (wired by initSSE)
     *
     * Detection is intentionally inclusive: the observer fires if the added
     * node *itself* matches, OR if any descendant matches. After a 10ms
     * settle delay, calls initializeNewElements which finds and initializes
     * everything not yet treact-initialized.
     *
     * This is the mechanism behind continuation-passing (CPS) workflows: a
     * server response can include a fresh `.treact` element with `load`
     * trigger, and the framework wires it up automatically.
     *
     * @returns {MutationObserver} Configured observer instance (not yet observing)
     */
    function createMutationObserver() {
        return new MutationObserver((mutations) => {
            let hasNewTreactElements = false;

            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) return;
                    const $node = $(node);

                    if ($node.hasClass('treact') || $node.is('[data-treact-sse]')) {
                        hasNewTreactElements = true;
                        debug('New treact-managed element detected:', node);
                    }
                    if ($node.find(CONFIG.SELECTOR + ', [data-treact-sse]').length > 0) {
                        hasNewTreactElements = true;
                        debug('New treact-managed elements detected inside:', node);
                    }
                });
            });

            if (hasNewTreactElements) {
                debug('Reinitializing new treact elements…');
                // Small delay to let the insertion settle.
                setTimeout(() => {
                    initializeNewElements();
                }, 10);
            }
        });
    }

    /**
     * Scan the document for new `.treact` and `[data-treact-sse]` elements
     * that haven't yet been initialized, and wire up each one.
     *
     * Called by the MutationObserver (with a 10ms settle delay) whenever new
     * treact-managed content is inserted. Idempotent: elements already marked
     * `data-treact-initialized` are skipped, and initSSE checks its own flag.
     *
     * Also re-runs `initDragAndDrop()` so any new `.treact-drag` elements
     * become draggable. And in CONFIG.DEBUG_MODE, re-runs `checkMistakes()`
     * to catch duplicate IDs or other hygiene issues in server-injected HTML.
     *
     * @returns {void}
     */
    function initializeNewElements() {
        const $newElements = $(CONFIG.SELECTOR).not('[data-treact-initialized]');
        if ($newElements.length > 0) {
            debug(`Found ${$newElements.length} new treact elements to initialize`);
            $newElements.each(function() {
                initializeSingleElement($(this));
            });
        }

        // SSE subscribers added after page-load (e.g. via tUpdate / tAppend).
        // initSSE is idempotent — it short-circuits on already-connected elements.
        $('[data-treact-sse]').each(function() {
            if (!$(this).data('treact-sse-initialized')) {
                initSSE(this);
            }
        });

        initDragAndDrop();

        // Re-validate DOM hygiene whenever new content lands, so server-injected
        // duplicate IDs (and similar) surface immediately.
        if (CONFIG.DEBUG_MODE && typeof window.checkMistakes === 'function') {
            window.checkMistakes();
        }
    }

    /**
     * Tear down treact bookkeeping for an element before it's removed.
     *
     * Called by the `remove` action executor (and should be called by anything
     * that programmatically deletes a treact element). Clears any active
     * interval associated with the element, unobserves from the
     * IntersectionObserver, removes treact event handlers (.treact namespace),
     * and clears framework-set data attributes. Recurses into descendants so
     * nested .treact children also get cleaned up.
     *
     * NOTE: not currently called by setContent / replaceWith / etc., which
     * replace inner HTML without invoking this. That's the known gap tracked
     * in TODO-fixes.md — would prevent the small interval-leak on `tUpdate`.
     *
     * @param {jQuery} $element - Element being removed
     * @returns {void}
     */
    function cleanupElement($element) {
        // Stop any intervals associated with this element
        const elementIntervalKey = $element.attr('id') || $element.data('treact-key');
        if (elementIntervalKey && activeIntervals.has(elementIntervalKey)) {
            clearInterval(activeIntervals.get(elementIntervalKey));
            activeIntervals.delete(elementIntervalKey);
            debug('Cleared interval for:', elementIntervalKey);
        }

        // Unobserve from intersection observer
        if (intersectionObserver && $element[0]) {
            intersectionObserver.unobserve($element[0]);
        }

        // Unbind treact-specific events
        $element.off('.treact');

        // Remove treact data
        $element.removeData('treact-initialized');
        $element.removeData('viewport-triggered');
        $element.removeData('in-viewport');

        // Cleanup children
        $element.find(CONFIG.SELECTOR).each(function() {
            cleanupElement($(this));
        });
    }

    /**
     * Open a Server-Sent Events connection on an element with `data-treact-sse`.
     *
     * The URL in `data-treact-sse` is decorated with the same element-state
     * query string as a regular AJAX request (so the SSE endpoint can read
     * `$_GET['data_X']` for context). Each SSE `message` event is parsed as a
     * JSON action array and applied via executeActions.
     *
     * Auto-reconnect with backoff:
     *   - Up to 5 attempts on error before giving up.
     *   - On graceful server-side close (`event: close`), stops cleanly.
     *
     * Idempotent: subsequent calls on an already-connected element short-circuit
     * via the `treact-sse-initialized` data flag.
     *
     * @param {jQuery|Element} element - Element carrying the data-treact-sse attribute
     * @returns {void}
     */
    function initSSE(element) {
        const $element = element instanceof jQuery ? element : $(element);
        let url = $element.attr('data-treact-sse');

        const elementData = collectElementData($element);

        const collectSelector = $element.attr('data-treact-collect');
        let collectedItems = {};

        if (collectSelector) {
            $(collectSelector).each(function(index) {
                collectedItems[index] = collectElementData(this);
            });
        }

        // ✅ BUILD QUERY STRING
        const allData = {
            ...elementData,
            ...(Object.keys(collectedItems).length > 0 && {
                data_collectedItems: collectedItems
            })
        };

        const queryString = $.param(allData);

        if (queryString) {
            url += (url.includes('?') ? '&' : '?') + queryString;
        }



        if (!url) {
            debug('[Treact SSE] No URL specified');
            return;
        }

        if ($element.data('treact-sse-initialized')) {
            debug('[Treact SSE] Already connected to:', url);
            return;
        }

        debug('[Treact SSE] Connecting to:', url);

        const eventSource = new EventSource(url);

        eventSource.onmessage = (event) => {
            try {
                const actions = JSON.parse(event.data);
                debug('[Treact SSE] Received actions:', actions);
                executeActions(actions);

                // ✅ Reset reconnect attempts on successful message
                $element.data('treact-sse-reconnect-attempts', 0);
            } catch (err) {
                console.error('[Treact SSE] Failed to parse actions:', err, event.data);
            }
        };

        eventSource.addEventListener('close', (event) => {
            debug('[Treact SSE] Server closed connection gracefully');
            closeSSE($element);
        });

        eventSource.onerror = (err) => {
            console.error('[Treact SSE] Connection error', err);

            // ✅ Optional: Implement max reconnect attempts
            const maxAttempts = 5;
            const attempts = $element.data('treact-sse-reconnect-attempts') || 0;

            if (attempts >= maxAttempts) {
                console.error('[Treact SSE] Max reconnection attempts reached, giving up');
                closeSSE($element);

                // Show user-friendly message
                executeActions([{
                    action: 'toast',
                    message: 'Connection lost. Please refresh the page.',
                    type: 'error',
                    duration: 0 // Don't auto-hide
                }]);
            } else {
                $element.data('treact-sse-reconnect-attempts', attempts + 1);
                debug(`[Treact SSE] Will retry... (attempt ${attempts + 1}/${maxAttempts})`);
                // Browser automatically reconnects
            }
        };

        eventSource.onopen = () => {
            debug('[Treact SSE] Connected successfully');
            $element.data('treact-sse-reconnect-attempts', 0);
        };

        $element.data('treact-sse-initialized', true);
        $element.data('treact-sse-connection', eventSource);

        debug('[Treact SSE] Initialized for element:', element);
    }


    /**
     * Force-close and re-open the SSE connection on an element.
     * Useful when the URL or context has changed and a fresh subscription is needed.
     *
     * @param {jQuery|Element} element - Element with active SSE connection
     * @returns {void}
     */
    function reconnectSSE(element) {
        const $element = element instanceof jQuery ? element : $(element);

        debug('[Treact SSE] Reconnecting:', element);

        closeSSE($element);

        // Small delay to ensure clean disconnect
        setTimeout(() => {
            initSSE($element);
        }, 100);
    }

    /**
     * Close a single element's SSE connection cleanly. Removes the connection
     * reference and the initialized flag so the element can be re-subscribed
     * later if desired.
     *
     * @param {jQuery|Element} element - Element to disconnect
     * @returns {void}
     */
    function closeSSE(element) {
        const $element = $(element);
        const eventSource = $element.data('treact-sse-connection');

        if (eventSource) {
            debug('[Treact SSE] Closing connection for:', element);
            eventSource.close();
            $element.removeData('treact-sse-connection');
            $element.removeData('treact-sse-initialized');
        }
    }

    /**
     * Close every active SSE connection on the page. Called automatically on
     * window `beforeunload` so the server can detect disconnect immediately
     * instead of waiting for TCP timeout.
     *
     * @returns {void}
     */
    function closeAllSSE() {
        debug('[Treact SSE] Closing all connections');

        $('[data-treact-sse]').each(function() {
            closeSSE(this);
        });
    }


    $(window).on('beforeunload', function() {
        debug('[Treact SSE] Page unloading, closing all SSE connections');
        closeAllSSE();
    });
    // ========================================
    // 📱 MOBILE DRAG & DROP SUPPORT
    // ========================================

    /**
     * Inject a drag payload into a drop zone as real DOM attributes.
     *
     * After a successful drop (desktop or mobile), the drop zone needs to carry
     * the dragged data so collectElementData can pick it up on the subsequent
     * sendToServer call. Must use .attr() (sets the DOM attribute), NOT .data()
     * (which only stores in jQuery's cache and doesn't appear to getAllDataAttributes).
     *
     * Single source of truth for the dragged-attribute names — desktop and
     * mobile drop handlers both call this so the names stay in sync.
     *
     * @param {jQuery} $zone   - Drop zone element to receive the payload
     * @param {Object} dragData - The dragged element's serialized data attributes;
     *                            specifically picks up .dragcontent, .id, .info
     * @returns {void}
     */
    function applyDragPayload($zone, dragData) {
        if (!dragData) return;
        if (dragData.dragcontent !== undefined) $zone.attr('data-dragcontent', dragData.dragcontent);
        if (dragData.id !== undefined) $zone.attr('data-dragged-id', dragData.id);
        if (dragData.info !== undefined) $zone.attr('data-dragged-info', dragData.info);
    }

    var mobileDragState = {
        draggedElement: null,
        dragData: null,
        touchClone: null,
        isActive: false
    };

    /**
     * Wire up touch-based drag-and-drop for `.treact-drag` sources to
     * `.treact-drop` zones. HTML5 native drag-and-drop doesn't work on touch
     * devices; this fills that gap by emulating the protocol with touchstart /
     * touchmove / touchend, including a position-following clone of the dragged
     * element for visual feedback.
     *
     * Detects the drop target via `document.elementFromPoint` (temporarily
     * hides the clone so it doesn't intercept). On successful drop, applies
     * the same `applyDragPayload` → `sendToServer` chain as desktop drops, so
     * the server-side handler is identical regardless of input modality.
     *
     * Idempotent: removes any prior `.treactTouch` listeners before re-binding,
     * so calling this multiple times doesn't stack handlers.
     *
     * Also triggers a brief haptic vibration on successful drop where available.
     *
     * @returns {void}
     */
    function enableMobileDragDrop() {
        // ✅ Remove any existing listeners first (prevents stacking!)
        $(document).off('.treactTouch');

        // ✅ Use namespaced events (.treactTouch) so we can remove them
        $(document).on('touchstart.treactTouch', '.treact-drag', function(e) {
            // ✅ Prevent multiple activations
            if (mobileDragState.isActive) {
                return;
            }

            mobileDragState.isActive = true;
            mobileDragState.draggedElement = $(this);
            mobileDragState.dragData = getAllDataAttributes(mobileDragState.draggedElement);

            // ✅ Prevent page scrolling while dragging
            e.preventDefault();

            // Visual feedback - create a clone that follows finger
            mobileDragState.touchClone = mobileDragState.draggedElement.clone()
                .addClass('touch-dragging-clone')
                .css({
                    position: 'fixed',
                    zIndex: 9999,
                    opacity: 0.7,
                    pointerEvents: 'none',
                    width: mobileDragState.draggedElement.outerWidth(),
                    height: mobileDragState.draggedElement.outerHeight()
                });

            $('body').append(mobileDragState.touchClone);
            mobileDragState.draggedElement.addClass('dragging');

            // Position clone at touch point
            var touch = e.originalEvent.touches[0];
            mobileDragState.touchClone.css({
                left: touch.pageX - (mobileDragState.draggedElement.outerWidth() / 2),
                top: touch.pageY - (mobileDragState.draggedElement.outerHeight() / 2)
            });

            debug('📱 Touch drag started:', mobileDragState.dragData);
        });

        $(document).on('touchmove.treactTouch', function(e) {
            if (!mobileDragState.isActive || !mobileDragState.touchClone) {
                return;
            }

            // ✅ Prevent page scrolling
            e.preventDefault();

            // Move the clone with the finger
            var touch = e.originalEvent.touches[0];
            mobileDragState.touchClone.css({
                left: touch.pageX - (mobileDragState.draggedElement.outerWidth() / 2),
                top: touch.pageY - (mobileDragState.draggedElement.outerHeight() / 2)
            });

            // Find drop target under the finger
            mobileDragState.touchClone.hide(); // Hide clone temporarily
            var elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
            mobileDragState.touchClone.show();

            // Highlight drop zones
            $('.treact-drop').removeClass('drag-over');
            if (elementBelow) {
                var $dropZone = $(elementBelow).closest('.treact-drop');
                if ($dropZone.length) {
                    $dropZone.addClass('drag-over');
                }
            }
        });

        $(document).on('touchend.treactTouch touchcancel.treactTouch', function(e) {
            if (!mobileDragState.isActive) {
                return;
            }

            var wasSuccessfulDrop = false;

            // Only process drop on touchend (not touchcancel)
            if (e.type === 'touchend') {
                var touch = e.originalEvent.changedTouches[0];
                mobileDragState.touchClone.hide();
                var elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);

                if (elementBelow) {
                    var $dropZone = $(elementBelow).closest('.treact-drop');

                    if ($dropZone.length) {
                        debug('📱 Touch drop on:', $dropZone);

                        // Inject dragged data using the same attribute names as the
                        // desktop path so the server sees one consistent payload.
                        applyDragPayload($dropZone, mobileDragState.dragData);

                        // Create a fake drop event
                        var fakeDropEvent = $.Event('drop', {
                            type: 'drop',
                            pageX: touch.pageX,
                            pageY: touch.pageY
                        });

                        // Trigger Treact flow
                        sendToServer($dropZone, fakeDropEvent);

                        wasSuccessfulDrop = true;

                        // Haptic feedback
                        if (navigator.vibrate) {
                            navigator.vibrate(10);
                        }
                    }
                }
            }

            // ✅ CLEANUP - Reset everything!
            if (mobileDragState.draggedElement) {
                mobileDragState.draggedElement.removeClass('dragging');
            }
            $('.treact-drop').removeClass('drag-over');

            if (mobileDragState.touchClone) {
                mobileDragState.touchClone.remove();
            }

            // ✅ Reset state
            mobileDragState.draggedElement = null;
            mobileDragState.dragData = null;
            mobileDragState.touchClone = null;
            mobileDragState.isActive = false;

            debug('📱 Touch drag ended. Success:', wasSuccessfulDrop);
        });
    }

    /**
     * Set up drag sources within a container. Finds every `.treact-drag` element
     * that doesn't already have `draggable="true"` and wires its dragstart /
     * dragend handlers. The dragged element's full `data-*` attribute set is
     * serialized as JSON into the dataTransfer payload.
     *
     * Also calls enableMobileDragDrop() so touch-based drops also work.
     *
     * Idempotent per element: elements already marked `draggable="true"` are
     * skipped, so it's safe to call repeatedly (e.g. after new drag sources
     * are inserted via tAppend).
     *
     * @param {jQuery} [$container=$(document)] - Scope to search; defaults to document
     * @returns {void}
     */
    function initDragAndDrop($container) {
        $container = $container || $(document);

        // Setup draggable elements (these are NOT Treact elements)
        $container.find('.treact-drag').each(function() {
            var $el = $(this);

            if ($el.attr('draggable') === 'true') return;

            $el.attr('draggable', 'true')
                .on('dragstart', function(e) {
                    var dragData = getAllDataAttributes($el);
                    e.originalEvent.dataTransfer.setData('application/json', JSON.stringify(dragData));
                    e.originalEvent.dataTransfer.effectAllowed = 'copy';
                    $el.addClass('dragging');
                    debug('🎯 Dragging:', dragData);
                })
                .on('dragend', function(e) {
                    $el.removeClass('dragging');
                });
        });

        // ✅ Mobile/touch drag support
        enableMobileDragDrop();
        // ✅ Drop zones are handled by bindEventHandlers() now!
        // No need to setup .treact-drop here!
    }

    /**
     * Install the global `popstate` listener — the half of treact's history
     * integration that handles browser back / forward navigation.
     *
     * When the user clicks back (or forward), `popstate` fires with `event.state`
     * containing whatever was stored at `pushHistory`/`replaceHistory` time
     * (typically {handler, data, path, title}). We re-issue a POST to that
     * handler with that data, then apply the actions — minus any pushHistory
     * or replaceHistory actions, which would create an infinite loop.
     *
     * Called once at module init time. The handler installs itself on the
     * window and remains for the page lifetime.
     *
     * @returns {void}
     */
    function initializePopStateHandler() {
        debug('🎯 Initializing popstate handler');

        window.addEventListener('popstate', function(event) {
            debug('🔙 POPSTATE FIRED!', {
                state: event.state,
                url: window.location.href,
                processingHistory: processingHistory
            });

            if (processingHistory) {
                debug('⏭️ Skipping - already processing');
                return;
            }

            // Get path from URL
            const urlParams = new URLSearchParams(window.location.search);
            const path = urlParams.get(CONFIG.historyParam);

            debug('📍 Path from URL:', path);

            if (!path) {
                debug('⚠️ No path in URL');
                return;
            }

            // Check if we have state data (handler info)
            if (event.state && event.state.handler) {
                debug('✅ Have state with handler:', event.state.handler);

                processingHistory = true;


                // Re-execute the handler with stored data
                $.ajax({
                    url: CONFIG.ajaxUrl,
                    method: 'POST',
                    contentType: 'application/json', // ← Add this
                    data: JSON.stringify({ // ← Use JSON.stringify
                        handler: event.state.handler,
                        data: event.state.data || {}
                    }),
                    dataType: 'json',
                    success: function(response) {
                        debug('📨 Popstate handler response:', response);

                        // Response is already the actions array
                        if (Array.isArray(response)) {
                            // Filter out history actions to avoid loops
                            const filteredActions = response.filter(action =>
                                action.action !== 'pushHistory' &&
                                action.action !== 'replaceHistory'
                            );

                            debug('🎬 Executing', filteredActions.length, 'actions (filtered from', response.length, ')');
                            filteredActions.forEach(action => debug('▶️', action.action));

                            // Execute the filtered actions
                            executeActions(filteredActions);
                            processingHistory = false;
                        } else {
                            console.error('❌ Unexpected response format:', response);
                        }
                    },
                    error: function(xhr, status, error) {
                        console.error('❌ Popstate handler failed:', error);
                        processingHistory = false;
                    }
                });
            } else {
                debug('⚠️ No state data - would reload');
                // For testing, don't reload yet
                // window.location.reload();
            }
        });

        debug('✅ Popstate handler registered');
    }

    /**
     * Push a new entry to the browser history stack.
     *
     * Stores the request recipe in the state object so that a later popstate
     * can re-run the same handler with the same data. Updates the URL to reflect
     * the new `path` (via buildHistoryUrl) and optionally the document title.
     *
     * Self-guards against re-entry: if we're currently *processing* a popstate
     * (replaying a prior request), pushHistory becomes a no-op to prevent
     * infinite history-growth loops.
     *
     * @param {Object} action
     * @param {string} action.path       - The path to embed as `?path=...` in the URL
     * @param {string} action.handler    - Handler name to re-invoke on popstate
     * @param {Object} [action.data]     - Payload data to re-send on popstate
     * @param {string} [action.title]    - Optional new document title
     * @returns {void}
     */
    function executePushHistory(action) {
        if (processingHistory) {
            debug('pushHistory: Skipping (processing popstate)');
            return;
        }

        const url = buildHistoryUrl(action.path);
        const title = action.title || document.title;

        const state = {
            path: action.path,
            handler: action.handler,
            data: action.data,
            timestamp: Date.now()
        };

        debug('🔵 BEFORE pushState:', {
            currentState: history.state,
            currentURL: window.location.href
        });

        history.pushState(state, title, url);

        debug('🟢 AFTER pushState:', {
            newState: history.state,
            newURL: window.location.href,
            historyLength: history.length
        });

        if (title) {
            document.title = title;
        }

        debug(`pushHistory: Created history entry for "${action.path}"`, state);
    }

    /**
     * Replace the current history entry instead of adding a new one.
     *
     * Same shape as pushHistory but uses `history.replaceState`. Useful for
     * attaching state to the entry the user is already on (e.g., on initial
     * page load) so that future back-presses can restore it correctly, without
     * generating an extra history step the user would have to back through.
     *
     * @param {Object} action
     * @param {string} action.path    - URL path to set
     * @param {string} action.handler - Handler name to bind to this entry
     * @param {Object} [action.data]  - Payload to replay on popstate
     * @param {string} [action.title] - Optional document title
     * @returns {void}
     */
    function executeReplaceHistory(action) {
        if (processingHistory) {
            debug('replaceHistory: Skipping (processing popstate)');
            return;
        }

        const url = buildHistoryUrl(action.path);
        const title = action.title || document.title;

        debug(`replaceHistory: Replacing history entry for "${action.path}"`, {
            url: url,
            title: title,
            handler: action.handler,
            data: action.data
        });

        const state = {
            path: action.path,
            handler: action.handler,
            data: action.data,
            timestamp: Date.now()
        };

        history.replaceState(state, title, url);

        if (title) {
            document.title = title;
        }
    }

    /**
     * Construct a URL with `?path=<path>` appended, preserving any other existing
     * query parameters but stripping the prior path entry.
     *
     * Uses CONFIG.baseUrl as the base (defaults to ''). The query-parameter name
     * itself is CONFIG.historyParam (defaults to 'path'). So default behavior:
     *   ('docs/install')         →  '?path=docs/install'
     *   ('home')  with ?utm=foo  →  '?utm=foo&path=home'
     *
     * @param {string} path - The path value to embed as the history-param's value
     * @returns {string} Constructed URL suitable for history.pushState / replaceState
     */
    function buildHistoryUrl(path) {
        // Get base URL without query string
        let baseUrl = CONFIG.baseUrl;
        if (baseUrl.includes('?')) {
            baseUrl = baseUrl.split('?')[0];
        }

        // Collect all parameters to keep
        const params = [];

        // Parse and preserve existing parameters (except path)
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.forEach((value, key) => {
            if (key !== CONFIG.historyParam) {
                params.push(`${key}=${value}`);
            }
        });

        // Add the new path parameter
        if (path) {
            params.push(`${CONFIG.historyParam}=${path}`);
        }

        // Build final URL
        if (params.length > 0) {
            return baseUrl + '?' + params.join('&');
        }

        return baseUrl;
    }
    /**
     * Play an audio file. Implementation of the `playSound` action.
     *
     * Modern browsers gate audio playback behind a prior user gesture (autoplay
     * policy); the function silently warns to console if `.play()` is rejected
     * for that reason. Tracks the audio instance in `treactActiveSounds` so it
     * can later be stopped via `stopAllSounds()`.
     *
     * @param {string}  url            - Audio file URL
     * @param {number} [volume=1.0]    - 0.0 (silent) to 1.0 (max)
     * @param {boolean} [loop=false]   - Whether to repeat indefinitely
     * @returns {void}
     */
    function playSound(url, volume = 1.0, loop = false) {
        try {
            const audio = new Audio(url);
            audio.volume = volume;
            audio.loop = loop;

            // Track this sound
            treactActiveSounds.push(audio);

            // Remove from tracking when done (if not looping)
            if (!loop) {
                audio.addEventListener('ended', () => {
                    const index = treactActiveSounds.indexOf(audio);
                    if (index > -1) {
                        treactActiveSounds.splice(index, 1);
                    }
                });
            }

            // Play the sound
            audio.play().catch(err => {
                console.warn('Treact: Could not play sound:', err.message);
                // Note: Browsers require user interaction before playing audio
                // This is a browser security feature, not a Treact bug
            });

        } catch (err) {
            console.error('Treact: Error playing sound:', err);
        }
    }

    /**
     * Stop every sound previously started with `playSound()`. Pauses each
     * tracked audio element, rewinds it to t=0, then clears the registry.
     * Implementation of the `stopAllSounds` action.
     *
     * Does not stop speech (use speechSynthesis.cancel() for that — see speakText).
     *
     * @returns {void}
     */
    function stopAllSounds() {
        treactActiveSounds.forEach(audio => {
            audio.pause();
            audio.currentTime = 0;
        });
        treactActiveSounds.length = 0; // Clear array
    }

    /**
     * Speak text via the browser's Web Speech API. Implementation of the
     * `speak` action. Any in-progress utterance is cancelled first so calls
     * don't queue up indefinitely.
     *
     * Silently no-ops (with a console warning) on browsers without
     * `speechSynthesis` support — current Firefox/Chrome/Safari all support it.
     *
     * @param {string}  text          - Text to speak
     * @param {string} [lang='en-US'] - BCP-47 tag (e.g. 'fr-FR', 'es-ES')
     * @param {number} [rate=1.0]     - 0.1 (slow) to 10 (fast); 1.0 = natural
     * @param {number} [pitch=1.0]    - 0 (low) to 2 (high); 1.0 = natural
     * @param {number} [volume=1.0]   - 0.0 (silent) to 1.0 (max)
     * @returns {void}
     */
    function speakText(text, lang = 'en-US', rate = 1.0, pitch = 1.0, volume = 1.0) {
        if (!('speechSynthesis' in window)) {
            console.warn('Treact: Speech synthesis not supported in this browser');
            return;
        }

        try {
            // Cancel any ongoing speech
            window.speechSynthesis.cancel();

            const utterance = new SpeechSynthesisUtterance(text);
            utterance.lang = lang;
            utterance.rate = rate;
            utterance.pitch = pitch;
            utterance.volume = volume;

            window.speechSynthesis.speak(utterance);

        } catch (err) {
            console.error('Treact: Error speaking text:', err);
        }
    }

    /**
     * Bootstrap: scan the document (or a given subtree) for treact elements and
     * SSE subscribers, then wire up the global drag/drop handlers. Called once
     * on DOMContentLoaded, and again whenever the MutationObserver promotes a
     * fragment via initializeNewElements().
     *
     * Distinct from initializeNewElements:
     *   - This function is the *top-level entry point* — wired to ready/load.
     *   - It does a global pass over CONFIG.SELECTOR (`.treact`) and ALL `[data-treact-sse]`.
     *   - initializeNewElements is the *per-mutation* entry point — narrower scope.
     *
     * @param {Document|Element|jQuery} [context=document] - Scope to initialize within (mostly for SSE limiting)
     * @returns {void}
     */
    function initializeTreact(context = document) {
        const $context = $(context); // Le contexte est pour les SSE
        debug('Initializing treact elements...');

        $(CONFIG.SELECTOR).each(function() {
            initializeSingleElement($(this));
        });

        //~ let $context = $(document);

        // Initialize SSE connections
        const $sseElements = $context.find('[data-treact-sse]').addBack('[data-treact-sse]');

        $sseElements.each(function() {
            initSSE(this);
        });

        // Laissé par N
        if ($context.is('[data-treact-sse]')) {
            initSSE($context[0]);
        }

        initDragAndDrop(null);

        debug('Initialization complete');
    }

    /**
     * Public API — surface exposed on `window.Treact`. This is the only
     * intentional global; all framework internals stay inside the IIFE.
     *
     * @namespace Treact
     * @property {Function} init           - Full (re)init pass: scan & wire all .treact + [data-treact-sse] elements. `Treact.init(context?)`
     * @property {Function} reinit         - Incremental init: scan only the given subtree (use after manual DOM mutations the MutationObserver missed). `Treact.reinit(context?)`
     * @property {Function} getValue       - Read the "current value" of an element, applying treact's value-of rules (input.val(), select.val(), [type=file] FormData, data-collect aggregation, .text()). Returns string or array.
     * @property {Function} send           - Programmatically fire a treact element as if it had been triggered. `Treact.send(selector)`
     * @property {Function} config         - Bulk-merge options into CONFIG. Returns updated CONFIG. `Treact.config({REQUEST_TIMEOUT: 60000, DEBUG_MODE: true})`
     * @property {Function} debug          - Toggle CONFIG.DEBUG_MODE. Enabling also runs the DOM-hygiene linter immediately. `Treact.debug(true)`
     * @property {Function} checkMistakes  - Run the DOM-hygiene linter on demand. `Treact.checkMistakes()`
     * @property {Function} cleanup        - Run cleanup hooks (cancel intervals, close SSE, drop muffin links) for every match. `Treact.cleanup(selector)`
     * @property {Function} setBaseUrl     - Override CONFIG.baseUrl. Useful when serving the SPA from a path-prefixed domain.
     * @property {Function} getConfig      - Shallow copy of CONFIG for inspection without exposing the live reference.
     */
    window.Treact = {
        init: initializeTreact,
        reinit: initializeNewElements,
        getValue: function(selector) {
            return getElementValue($(selector));
        },
        send: function(selector) {
            sendToServer($(selector));
        },
        config: function(options) {
            Object.assign(CONFIG, options);
            return CONFIG;
        },
        debug: function(enabled) {
            CONFIG.DEBUG_MODE = enabled;
            if (enabled) {
                runCheckMistakes();
            }
        },
        checkMistakes: function() {
            runCheckMistakes();
        },
        cleanup: function(selector) {
            $(selector).each(function() {
                cleanupElement($(this));
            });
        },
        setBaseUrl: function(url) {
            CONFIG.baseUrl = url;
        },
        getConfig: function() {
            return $.extend({}, CONFIG);
        }
    };

    /**
     * Auto-initialize on document ready
     */
    $(document).ready(function() {
        debug('Document ready, initializing Treact...');
        initializeTreact();

        // Start watching for dynamically added content
        mutationObserver = createMutationObserver();
        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
        debug('MutationObserver started - watching for dynamic content');

        // In debug mode, surface common DOM-hygiene issues (duplicate IDs,
        // attributes with stray CSS syntax, etc.) once the page has settled.
        if (CONFIG.DEBUG_MODE) {
            runCheckMistakes();
        }
    });

})(jQuery);
//~ // Manually reinitialize new elements
//~ Treact.reinit();

//~ // Clean up specific element before removal
//~ Treact.cleanup('#my-element');

//~ // Initialize specific element
//~ $('#new-element').addClass('treact').attr('data-handler', 'myHandler');
//~ Treact.reinit();
